import React, { useContext, useEffect, useCallback, useMemo } from 'react';
import { Box, Button, Typography, useTheme } from "@mui/material";
import { Groups, TrendingUp, EmojiEvents } from "@mui/icons-material";
import Lottie from "lottie-react";
import { Context as AppContext } from "../../context/AppContext";
import { useNavigate } from "react-router-dom";
import ListOfInternships from "./ListOfInterships";
import { MainLayout } from "../../layouts/main-layout/MainLayout";
import animationData from '../../assets/mentor.json';
import './mentorHomepage.css';
import { toast } from "react-toastify";
import { capitalizeFirstWord } from '../../utils/functions';
const MentorHomepage = () => {
    const theme = useTheme();
    const {
        state: { currentUser, interns, internships },
        updateUserData,
        getInterns,
        getInternships,
        sendMatchedEmails
    } = useContext(AppContext);
    const navigate = useNavigate();

    const hasMentees = useMemo(() => {
        return currentUser?.mentees?.length > 0;
    }, [currentUser?.mentees]);

    const isMentorActive = useMemo(() => {
        return hasMentees || currentUser?.isMentor;
    }, [hasMentees, currentUser?.isMentor]);

    useEffect(() => {
        if (currentUser?.mentees?.length > 0) {
            getInterns(currentUser.mentees);
            getInternships({ uid: currentUser.uid, type: currentUser.role });
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.uid, currentUser?.mentees, currentUser?.role]);

    const handleSetMentorVisible = useCallback(async () => {
        try {
            const updatedUser = { ...currentUser, isMentor: true };
            await updateUserData(updatedUser);
            toast.success('You are now a mentor!');
        } catch (error) {
            toast.error('Failed to update mentor status. Please try again.');
        }
    }, [currentUser, updateUserData]);

    const handleTestEmail = useCallback(async () => {
        try {
            await sendMatchedEmails(
                "contact@aiden.global",
                currentUser.name || 'Test Mentor',
                'urosch1995@gmail.com',
                'Test Mentee',
                'AIDEN Industry Horizons'
            );
            toast.success('Test emails sent successfully!');
        } catch (error) {
            console.error('Error sending test emails:', error);
            toast.error('Failed to send test emails. Please try again.');
        }
    }, [currentUser, sendMatchedEmails]);

    return (
        <MainLayout>
            <Box sx={{
                minHeight: '100vh',
                bgcolor: '#fafbfc',
                fontFamily: 'Poppins, system-ui, -apple-system, sans-serif'
            }}>
                <Box sx={{
                    maxWidth: '1200px',
                    mx: 'auto',
                    px: { xs: 3, md: 4 },
                    py: { xs: 3, md: 5 }
                }}>
                    {/* Welcome Header */}
                    <Box sx={{ mb: 5 }}>
                        <Typography
                            variant="h4"
                            sx={{
                                fontSize: { xs: '1.5rem', md: '2rem' },
                                fontWeight: 600,
                                color: '#1a1a1a',
                                mb: 1,
                                letterSpacing: '-0.02em'
                            }}
                        >
                            Welcome back,
                            <Box component="span" sx={{
                                color: theme.palette.primary.main,
                                fontWeight: 700
                            }}>
                                {' '}{capitalizeFirstWord(currentUser?.name)}!
                            </Box>
                        </Typography>
                        
                    </Box>

                    {isMentorActive ? (
                        <Box sx={{
                            bgcolor: 'white',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                            p: { xs: 3, md: 4 },
                            transition: 'box-shadow 0.2s ease-in-out',
                            '&:hover': {
                                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)'
                            }
                        }}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mb: 3,
                                flexWrap: 'wrap',
                                gap: 2
                            }}>
                                {/* <Typography
                                    variant="h5"
                                    sx={{
                                        fontWeight: 600,
                                        color: '#1e293b',
                                        fontSize: '1.25rem'
                                    }}
                                >
                                    Dashboard
                                </Typography> */}
                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                                    <Box sx={{
                                        px: 2,
                                        py: 0.5,
                                        bgcolor: theme.palette.secondary.main,
                                        color: theme.palette.primary.main,
                                        borderRadius: '20px',
                                        fontSize: '0.875rem',
                                        fontWeight: 500
                                    }}>
                                        Active Mentor
                                    </Box>

                                    {currentUser.email === "julie@aiden.global" && <Button
                                        onClick={handleTestEmail}
                                        variant="outlined"
                                        size="small"
                                        sx={{
                                            borderColor: theme.palette.primary.main,
                                            color: theme.palette.primary.main,
                                            fontSize: '0.75rem',
                                            fontWeight: 500,
                                            px: 2,
                                            py: 0.5,
                                            borderRadius: '12px',
                                            textTransform: 'none',
                                            minWidth: '100px',
                                            '&:hover': {
                                                bgcolor: theme.palette.primary.main + '10',
                                                borderColor: theme.palette.primary.main
                                            },
                                            transition: 'all 0.2s ease-in-out'
                                        }}
                                    >
                                        Test Email
                                    </Button>}
                                </Box>
                            </Box>
                            <ListOfInternships internships={internships} interns={interns} applications={false} disableCard={true} />
                        </Box>
                    ) : (
                        <Box sx={{
                            bgcolor: 'white',
                            borderRadius: '20px',
                            border: '1px solid #e2e8f0',
                            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.05)',
                            overflow: 'hidden',
                            transition: 'box-shadow 0.2s ease-in-out',
                            '&:hover': {
                                boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
                            }
                        }}>
                            <Box sx={{ p: { xs: 4, md: 6 }, textAlign: 'center' }}>
                                <Box sx={{ mb: 4 }}>
                                    <Typography
                                        variant="h5"
                                        sx={{
                                            fontWeight: 600,
                                            color: '#1e293b',
                                            mb: 2,
                                            fontSize: { xs: '1.25rem', md: '1.5rem' }
                                        }}
                                    >
                                        Ready to Start Mentoring?
                                    </Typography>
                                    <Typography
                                        variant="body1"
                                        sx={{
                                            color: '#64748b',
                                            fontSize: '1rem',
                                            maxWidth: '500px',
                                            mx: 'auto',
                                            lineHeight: 1.6
                                        }}
                                    >
                                        Start mentoring and help guide the next generation of talent to success.
                                    </Typography>
                                </Box>

                                <Box sx={{ mb: 5, display: 'flex', justifyContent: 'center' }}>
                                    <Lottie
                                        animationData={animationData}
                                        loop={true}
                                        style={{ height: '240px', width: '240px' }}
                                    />
                                </Box>

                                <Button
                                    onClick={handleSetMentorVisible}
                                    variant="contained"
                                    size="large"
                                    sx={{
                                        bgcolor: theme.palette.primary.main,
                                        color: 'white',
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        px: 4,
                                        py: 1.5,
                                        borderRadius: '12px',
                                        textTransform: 'none',
                                        minWidth: '180px',
                                        boxShadow: `0 4px 14px 0 ${theme.palette.primary.main}40`,
                                        '&:hover': {
                                            bgcolor: theme.palette.primary.dark || '#4c2a6b',
                                            boxShadow: `0 6px 20px 0 ${theme.palette.primary.main}59`,
                                            transform: 'translateY(-1px)'
                                        },
                                        '&:focus': {
                                            outline: `2px solid ${theme.palette.primary.main}`,
                                            outlineOffset: '2px'
                                        },
                                        transition: 'all 0.2s ease-in-out'
                                    }}
                                >
                                    Start Mentoring
                                </Button>

                                {/* Feature Cards */}
                                <Box sx={{
                                    mt: 6,
                                    pt: 4,
                                    borderTop: '1px solid #f1f5f9',
                                    display: 'grid',
                                    gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                                    gap: 3
                                }}>
                                    <Box sx={{ textAlign: 'center' }}>
                                        <Box sx={{
                                            width: 48,
                                            height: 48,
                                            bgcolor: '#f0f9ff',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 2
                                        }}>
                                            <Groups sx={{ fontSize: '1.5rem', color: '#3b82f6' }} />
                                        </Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                mb: 0.5
                                            }}
                                        >
                                            Guide Students
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#64748b' }}
                                        >
                                            Share your expertise
                                        </Typography>
                                    </Box>

                                    <Box sx={{ textAlign: 'center' }}>
                                        <Box sx={{
                                            width: 48,
                                            height: 48,
                                            bgcolor: '#f0fdf4',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 2
                                        }}>
                                            <TrendingUp sx={{ fontSize: '1.5rem', color: '#10b981' }} />
                                        </Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                mb: 0.5
                                            }}
                                        >
                                            Track Progress
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#64748b' }}
                                        >
                                            Monitor achievements
                                        </Typography>
                                    </Box>

                                    <Box sx={{ textAlign: 'center' }}>
                                        <Box sx={{
                                            width: 48,
                                            height: 48,
                                            bgcolor: '#fefce8',
                                            borderRadius: '12px',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mx: 'auto',
                                            mb: 2
                                        }}>
                                            <EmojiEvents sx={{ fontSize: '1.5rem', color: '#f59e0b' }} />
                                        </Box>
                                        <Typography
                                            variant="subtitle2"
                                            sx={{
                                                fontWeight: 600,
                                                color: '#1e293b',
                                                mb: 0.5
                                            }}
                                        >
                                            Make Impact
                                        </Typography>
                                        <Typography
                                            variant="body2"
                                            sx={{ color: '#64748b' }}
                                        >
                                            Change lives
                                        </Typography>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    )}
                </Box>
            </Box>
        </MainLayout>
    );
};

export default MentorHomepage;
